import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSubject } from "../../context/SubjectContext";
import MarkSubmissionHeader from "../../components/MarkSubmission/MarkSubmissionHeader";
import MarkSubmissionTable from "../../components/MarkSubmission/MarkSubmissionTable";
import styles from "./MarksEntryPage.module.css";
import { Toaster } from "react-hot-toast";
import { useCenter } from "../../context/CenterContext";
import { useLocation } from "react-router-dom";
import {
  MARK_STATUS,
  AUTO_SAVE_DELAY_MS,
} from "../../constants/Constants";
import { useApi } from "../../context/ApiClient";
import { useAuth } from "react-oidc-context";
import {
  getQualificationTitle,
  getCenterType,
  checkunitIsConsortiaUnit,
} from "../../utils/utils";

import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";
import { useUnits } from "../../context/UnitsContext";

const MarksEntryPage = () => {
  const { subjectData, subjects } = useSubject();
  const { selectedSubject, selectedSubjectCode, subjectId } = subjectData || {};

  const [candidates, setCandidates] = useState([]);
  const [errorVisible, setErrorVisible] = useState(false);
  const { centerNumber } = useCenter();
  const location = useLocation();
  const { unit, sessionId } = location.state || {};
  const { consortiaCentres = [] } = useConsortiaCenter();
  const { consortiaUnits = [] } = useUnits(); // still available if needed elsewhere
  const [availableAssignments, setAvailableAssignments] = useState([]);
  const [rows, setRows] = useState(() => {
    return candidates.map((candidate) => ({
      ...candidate,
      markAsAbsent: false,
      totalCriteriaAchieved: null,
      assignmentId: null,
    }));
  });
  const auth = useAuth();
  const token = auth.user?.access_token;
  const api = useApi(process.env.REACT_APP_AMS_BASE_URL);

  const unitcode = String(unit).split(" ")[0].trim().toUpperCase();

  const currentCentreType = getCenterType(consortiaCentres, Number(centerNumber), subjectData?.selectedSubject, subjectData?.selectedSubjectId) || "";

  const isConsortiaUnit = checkunitIsConsortiaUnit(
    consortiaCentres,
    centerNumber,
    sessionId,
    unitcode,
  );

  // Snapshot of last successfully saved rows (serialized)
  const lastSavedRowsRef = useRef(JSON.stringify([]));
  // Timestamp of last successful (manual or auto) save
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // One-time timer ref for fixed window autosave
  const autoSaveTimeoutRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  // Row completeness logic (updated to treat 0 as a valid mark)
  const isRowComplete = (row) => {
    // Absent rows are complete
    if (row.markAsAbsent) return true;

    // Marks must be present; 0 is valid
    const marks = row.totalCriteriaAchieved;
    const hasMarks =
      marks !== null &&
      marks !== undefined &&
      !(typeof marks === "string" && marks.trim() === "");

    if (!hasMarks) return false;

    // Assignment must be present (when not absent)
    const assignment = row.assignmentId;
    const hasAssignment =
      assignment !== null &&
      assignment !== undefined &&
      !(typeof assignment === "string" && assignment.trim() === "");

    if (!hasAssignment) return false;

    // Numeric validation
    const num = Number(marks);
    if (Number.isNaN(num)) return false;

    // Range checks
    const max = Number(row.totalMarks);
    if (Number.isNaN(max)) return false;

    // Allow 0…max inclusive
    if (num < 0 || num > max) return false;

    // Prevent leading zero patterns like "00", "01" (only when marks is a string)
    if (typeof marks === "string" && /^0\d+$/.test(marks)) return false;

    return true;
  };

  // Fetch initial candidate data
  useEffect(() => {
    const fetchCandidates = async () => {
      // Guard required inputs
      if (!centerNumber || !sessionId || !unit) return;

      // Wait until consortiaCentres are available (prefer a loading flag if your context provides it)
      if (!Array.isArray(consortiaCentres) || consortiaCentres.length === 0) {
        
        return;
      }

      try {
        const res = await api.get(
          currentCentreType === "Lead" && isConsortiaUnit
            ? "consortia/getConsortiaMarkSubmissionCandidateData"
            : "getMarkSubmissionCandidateData",
          {
            params: {
              centreNumber: centerNumber,
              sessionId: sessionId,
              assessmentCode: unitcode,
            },
          },
        );
        if (res.data.isSuccess) {
          const candidates = res.data.data.candidates;
          const availableAssignments = res.data.data.availableAssignments;
          setAvailableAssignments(availableAssignments);
          const candidateList = (candidates || []).map((item) => ({
            candidateNumber:
              currentCentreType === "Lead" && isConsortiaUnit
                ? String(item.centreCandidateNumber)
                : String(item.candidateNumber),
            ...(currentCentreType === "Lead" &&
              isConsortiaUnit && { centreNumber: item.centreNumber }),
            candidateName: item.candidateName,
            isSampled: item.isSampled,
            markStatus: item.markStatus,
          }));
          setCandidates(candidateList);

          // Build rows (for table entry state)
          const rowList = (candidates || []).map((item) => {
            const assignmentId = item.assignmentId ?? "";
            const selectedAssignment = availableAssignments.find(
              (a) => String(a.assignmentId) === String(assignmentId)
            );
            return {
              candidateNumber:
                currentCentreType === "Lead" && isConsortiaUnit
                  ? String(item.centreCandidateNumber)
                  : String(item.candidateNumber),
              ...(currentCentreType === "Lead" &&
                isConsortiaUnit && { centreNumber: item.centreNumber }),
              candidateName: item.candidateName,
              isSampled: item.isSampled,
              markStatus: item.markStatus,
              markAsAbsent: item.markAsAbsent || false,
              totalCriteriaAchieved: item.totalCriteriaAchieved ?? "",
              totalMarks: item.totalMarks ?? "",
              assignmentId: assignmentId,
              assignment: selectedAssignment?.assignmentLabel || "",
            };
          });
          setRows(rowList);

          // Initialize snapshot after initial load
          lastSavedRowsRef.current = JSON.stringify(rowList);
        }
      } catch (err) {
        console.error("Error fetching candidate data", err);
      }
    };
    // Important: include consortiaCentres in dependencies so this re-runs when they load
    fetchCandidates();
  }, [centerNumber, selectedSubjectCode, sessionId, unit, consortiaCentres]);

  // Aggregate invalid state
  const anyRowInvalid = useMemo(
    () => rows.some((row) => !isRowComplete(row)),
    [rows],
  );

  // Submit click handler
  const handleOcrSubmit = () => {
    if (anyRowInvalid) {
      setErrorVisible(true);
    } else {
      setErrorVisible(false);
      setShowModal(true);
    }
  };

  // Auto-hide error banner if everything becomes valid later
  useEffect(() => {
    if (errorVisible && !anyRowInvalid) {
      setErrorVisible(false);
    }
  }, [anyRowInvalid, errorVisible]);

  // Build payload identical to manual save
  const buildSavePayload = () => {
    const unitCode = unit;

    return rows.map((row) => ({
      candidateNumber: Number(row.candidateNumber),
      markAsAbsent: Boolean(row.markAsAbsent),
      totalCriteriaAchieved: row.totalCriteriaAchieved ?? null,
      assignmentId: row.assignmentId || null,
      markStatusId: MARK_STATUS.SAVED,
      centreNumber: Number(
        currentCentreType === "Lead" && isConsortiaUnit
          ? row.centreNumber || centerNumber
          : centerNumber,
      ),
      sessionId: sessionId,
      subjectId: Number(subjectId),
      assessmentCode: unitCode?.split(" ")[0],
    }));
  };

  // Silent auto-save (no toast)
  const handleAutoSave = async () => {
    const currentSerialized = JSON.stringify(rows);
    if (currentSerialized === lastSavedRowsRef.current) return;
    try {
      const payload = buildSavePayload();
      
      const autoSaveEndpoint =
        currentCentreType === "Lead" && isConsortiaUnit
          ? "consortia/saveOrUpdateCandidateMarks"
          : "/saveOrUpdateCandidateMarks";
      await api.post(autoSaveEndpoint, payload);
      lastSavedRowsRef.current = currentSerialized;
      setLastSavedAt(new Date());
      localStorage.setItem("marksRows", currentSerialized);
    } catch (e) {
      console.error("Auto-save failed", e);
    }
  };

  // Start a timer at the FIRST unsaved change, do NOT reset it on further edits.
  const serialized = JSON.stringify(rows);
  const hasUnsavedChanges = serialized !== lastSavedRowsRef.current;
  useEffect(() => {
    if (hasUnsavedChanges) {
      if (!autoSaveTimeoutRef.current) {
        autoSaveTimeoutRef.current = setTimeout(() => {
          handleAutoSave();
          autoSaveTimeoutRef.current = null; // allow new cycle after next edit
        }, AUTO_SAVE_DELAY_MS);
      }
    } else {
      // Data matches last saved snapshot → clear pending timer (if any)
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    }
  }, [rows]);

  // Cleanup on unmount (if user navigates away before timer fires)
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, []);

  // Attempt final synchronous save on unload
  const finalSaveIfNeeded = useRef(null);
  finalSaveIfNeeded.current = () => {
    
    const currentSerialized = JSON.stringify(rows);
    if (currentSerialized === lastSavedRowsRef.current) return;
    const payload = buildSavePayload();
   
    const autoSaveEndpoint =
      currentCentreType === "Lead" && isConsortiaUnit
        ? "consortia/saveOrUpdateCandidateMarks"
        : "saveOrUpdateCandidateMarks";

    const url = `${process.env.REACT_APP_AMS_BASE_URL}${autoSaveEndpoint}`;
    const data = JSON.stringify(payload);

    try {
      if (window.fetch) {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, false); // synchronous!
        xhr.setRequestHeader("Content-Type", "application/json");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.send(data);
        lastSavedRowsRef.current = currentSerialized;
        return;
      }
    } catch (e) {
      // Continue to beacon fallback
    }
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([data], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        lastSavedRowsRef.current = currentSerialized;
      }
    } catch (e) {
      console.error("Final save failed", e);
    }
  };

  // beforeunload (desktop browsers)
  useEffect(() => {
    const onBeforeUnload = () => {
      finalSaveIfNeeded.current && finalSaveIfNeeded.current();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [rows]);

  // pagehide + visibilitychange (covers mobile/tab lifecycle better)
  useEffect(() => {
    const onPageHide = () => {
      finalSaveIfNeeded.current && finalSaveIfNeeded.current();
    };
    const onVisChange = () => {
      if (document.visibilityState === "hidden") {
        finalSaveIfNeeded.current && finalSaveIfNeeded.current();
      }
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisChange);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisChange);
    };
  }, [rows]);

  // Toast success icon
  const SuccessIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="#FFFFFF"
        stroke="#2B8000"
        strokeWidth="2"
      />
      <path
        d="M8 12.5l2.5 2.5L16 9.5"
        fill="none"
        stroke="#2B8000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div className={styles.marksentrypage}>
      <div>
        <div className={styles.xcontainer}>
          <section
            className="section mt-0 py-0"
            style={{ paddingRight: "0rem !important" }}
          >
            <div className="pt-3 subject-header-fixed">
              <p className="is-size-6 has-text-weight-bold">
                {getQualificationTitle(
                  subjects,
                  subjectData?.selectedSubjectId,
                )}
              </p>
              <h1 className="title is-2 mb-0">
                {selectedSubject}{" "}
                <span className="is-size-6 has-text-weight-normal">
                  {selectedSubjectCode}
                </span>
              </h1>
              <p className="xbox xnotification is-info mt-0">
                Marks submission
              </p>
            </div>
          </section>
        </div>
        <div style={{ paddingLeft: "25px" }}>
          <MarkSubmissionHeader
            onSubmitClicked={handleOcrSubmit}
            showModal={showModal}
            setShowModal={setShowModal}
            rows={rows}
            setRows={setRows}
            candidates={candidates}
            isRowComplete={isRowComplete}
            onManualSaveSuccess={(savedRows) => {
              lastSavedRowsRef.current = JSON.stringify(savedRows);
              setLastSavedAt(new Date());
              if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = null;
              }
            }}
            lastSavedAt={lastSavedAt}
            isStage3={false}
            hasUnsavedChanges={hasUnsavedChanges}
            STORAGE_KEY={"marksRows"}
          />

          <Toaster
            position="top-right"
            containerStyle={{ marginTop: "8rem" }}
            toastOptions={{
              style: {
                width: "336px",
                borderRadius: "4px",
                padding: "1em",
                fontFamily:
                  "'Open Sans', 'Source Sans 3', Arial, Helvetica, sans-serif",
                boxShadow:
                  "rgba(0, 0, 0, 0.14) 0px 8px 12px -4px, rgba(0, 0, 0, 0.24) 0px 4px 6px -2px",
              },
              success: {
                style: {
                  backgroundColor: "#f5faf5",
                  border: "1px solid #2B8000",
                  color: "#2B8000",
                },
                icon: <SuccessIcon />,
              },
            }}
          />

          <MarkSubmissionTable
            rows={rows}
            setRows={setRows}
            availableAssignments={availableAssignments}
            showError={errorVisible}
            candidates={candidates}
            anyRowInvalid={anyRowInvalid}
            isRowComplete={isRowComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default MarksEntryPage;
